import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { REGION_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  Region,
  RegionSearch,
  RegionSortOrQuery,
} from 'src/app/model/locations/region';
import { RegionApiService } from 'src/app/services/api/region-api.service';
import { RegionTableService } from 'src/app/services/table/region-table.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { REGION_TABLE_DEFINITION } from '../../definitions/region-table-definition';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { FilterMetadata } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Panel } from '@openng/optimus-ui/panel';
import { RegionMobileFilterComponent } from '../../components/region-mobile-filter/region-mobile-filter.component';

@Component({
  selector: 'app-region-container',
  templateUrl: './region-container.component.html',
  styleUrls: ['./region-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    Panel,
    RegionMobileFilterComponent,
    TranslatePipe,
  ],
})
export class RegionContainerComponent
  extends SearchContainerComponent<RegionSearch>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _regionApi = inject(RegionApiService);
  protected _regionTableService = inject(RegionTableService);
  protected _authApi = inject(AuthApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);

  allRegions: any[] = [];
  canCreate = false;
  canEdit = false;
  mobileFilterCollapsed = true;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.setTableService(this._regionTableService);

    this.initialize();
  }

  initialize() {
    this._translateService.get('LOCATION.SCREEN.REGION').subscribe((label) => {
      if (this._deviceService.isMobile()) {
        this._breadCrumbBuilderService.resetAndAddBreadCrumb({
          label: label,
          routerLink: null,
        });
      } else {
        this._tabService.updateActiveTabLabel(label);
      }
      this.screenName = label;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.REGIONS.CREATE_REGIONS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.REGIONS.EDIT_REGIONS,
    ]);

    this.columns = REGION_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  clearTable() {
    this.clearScreen();
    this.allRegions = [];
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._regionApi
      .queryForRegions(req.pageSize, req.page, query as RegionSortOrQuery, sort)
      .subscribe({
        next: (regionConnection) => {
          const regions = regionConnection.items;
          this.elements = [...regions];
          this.totalRecords = regionConnection.totalRecords;
          this._regionTableService.setLastTotalRecords(this.totalRecords);
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this.loading = false;
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'LOCATION.MESSAGES.HEADERS.QUERY_REGION',
            bodyKey: 'LOCATION.MESSAGES.ERROR.QUERY_REGION_ERROR',
          });
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = {
      name: null,
      contactList: null,
      owner: null,
      areas: null,
    };

    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (filters.contactList) {
      query.contactList = (filters.contactList as FilterMetadata).value;
    }
    if (filters.owner) {
      query.owner = (filters.owner as FilterMetadata).value;
    }
    if (filters.areas) {
      query.areas = (filters.areas as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._regionTableService.getLastLazyLoad()! });
  }

  newRegion() {
    const tab = this._tabService.buildNewTab(REGION_DETAIL_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  changeViewEvent($event: { data: Region }) {
    const tab = this._tabService.buildNewTab(
      REGION_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  mobileFilter() {
    const newLazy: any = {
      filters: {},
    };
    this.mobileFilterCollapsed = true;
    this._regionTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._regionTableService.getLastLazyLoad()! });
  }
}
